import React, { useContext, useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Row, Table, Button, Badge } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";

import Header from "../../../components/Layout/Header";
import Sidebar from "../../../components/Layout/Sidebar";
import Main from "../../../components/Layout/Main";
import Pagination from "../../../components/Layout/Pagination";
import Spinner from "../../../components/Layout/spinner";
import SweetAlertWrapper from "../../../components/Layout/sweetAlert";

import CustomersContext from "../../../context/otogas/customers/customersContext";
import AuthContext from "../../../context/auth/authContext";
import { download_partnership_sales } from '../../../api/otogas';

import userCan from "../../../config/user_can";
import { formatDate as formatteDate } from "../../../api/util";

const ListPartnershipSales = () => {
    const navigate = useNavigate();

    // ===== Contexts =====
    const customersContext = useContext(CustomersContext);
    const {
        partnership_sales = [],
        partnership_salesTotalRecords = 0,
        listPartnershipSales, // (partnerCode, stationCode, shiftNumber, startDate, endDate, page, size, orderByColumn, isDescending)
        notification,
        clear_notification,

        // ✅ NEW: sums from context (don’t compute in UI)
        totalLitres = 0,
        totalAmounts = 0,
    } = customersContext;

    const authContext = useContext(AuthContext);
    const { user } = authContext;

    // ===== UI State =====
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(15);

    const [filters, setFilters] = useState({
        shiftNumber: "",
        startDate: "",
        endDate: "",
        orderByColumn: "DateCreated",
        isDescending: true,
    });

    const [loading, setLoading] = useState(false);
    const [download_loading, setDownloadLoading] = useState(false);

    const hasActiveFilters = useMemo(() => {
        const hasShift = !!String(filters.shiftNumber || "").trim();
        const hasDates = !!(filters.startDate || filters.endDate);
        return hasShift || hasDates;
    }, [filters.shiftNumber, filters.startDate, filters.endDate]);

    const downloadLabel = hasActiveFilters ? "Selected Range Sales" : "Month to Date Sales";
    // SweetAlert
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState({ text: "", type: "" });

    // Gate so we don’t show “no partner code” before user loads
    const [authReady, setAuthReady] = useState(false);

    useEffect(() => {
        if (user && typeof user === "object") setAuthReady(true);
    }, [user]);

    useEffect(() => {
        if (notification) {
            setAlertMessage({
                text: notification.text || "Done",
                type: notification.type || "info",
            });
            setShowAlert(true);
        }
    }, [notification]);

    // ===== PartnerCode from user (support multiple casings) =====
    const userPartnerCode = useMemo(() => {
        if (!user) return "";
        return String(user.PartnerCode ?? user.partnerCode ?? user.partner_code ?? "").trim();
    }, [user]);

    // ===== Helpers =====
    const parseDate = (dateStr) => (dateStr ? new Date(dateStr) : null);

    // ✅ Avoid timezone “-1 day” issue by formatting from LOCAL date parts
    const formatLocalYYYYMMDD = (date) => {
        if (!date) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const handleDateRangeChange = (dates) => {
        const [start, end] = dates;
        setCurrentPage(1);
        setFilters((prev) => ({
            ...prev,
            startDate: formatLocalYYYYMMDD(start),
            endDate: formatLocalYYYYMMDD(end),
        }));
    };

    const toPascalCase = (str) =>
        (str || "")
            .replace(/(^\w|[A-Z]|\b\w)/g, (m) => m.toUpperCase())
            .replace(/[\s_]+/g, "");

    // Column sorting (same idea as ListSales)
    const handleSort = (column) => {
        const formattedColumn = toPascalCase(column);
        setCurrentPage(1);
        setFilters((prev) => ({
            ...prev,
            orderByColumn: formattedColumn,
            isDescending: prev.orderByColumn === formattedColumn ? !prev.isDescending : true,
        }));
    };

    const fetchSales = async (page, size, f) => {
        if (!listPartnershipSales || !authReady || !userPartnerCode) return;

        try {
            setLoading(true);

            await listPartnershipSales(
                userPartnerCode,
                "", // stationCode – no station filters for now
                String(f.shiftNumber || "").trim(),
                f.startDate || "", // yyyy-MM-dd (local-safe)
                f.endDate || "",
                page,
                size,
                f.orderByColumn || "DateCreated",
                !!f.isDescending
            );
        } finally {
            setLoading(false);
        }
    };

    // Auto fetch when partner is present and filters change
    useEffect(() => {
        if (!authReady) return;
        if (!userPartnerCode) return;

        fetchSales(currentPage, itemsPerPage, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        authReady,
        userPartnerCode,
        currentPage,
        itemsPerPage,
        filters.shiftNumber,
        filters.startDate,
        filters.endDate,
        filters.orderByColumn,
        filters.isDescending,
    ]);

    const handleApplyFilters = () => {
        setCurrentPage(1);
        fetchSales(1, itemsPerPage, filters);
    };

    const handleReset = () => {
        setCurrentPage(1);
        setFilters({
            shiftNumber: "",
            startDate: "",
            endDate: "",
            orderByColumn: "DateCreated",
            isDescending: true,
        });
    };

    const handle_downloads = async (func, setLoading) => {
        setLoading(true);
        try {
            const res = await func();
            if (res === "File not found") {
                setAlertMessage({
                    text: "No transactions found",
                    type: "info",
                });
                setShowAlert(true);
            } else if (res === "You are not authorized to download this file") {
                setAlertMessage({
                    text: "You are not authorized to download this file",
                    type: "info",
                });
                setShowAlert(true);
            } else {
                // File download handled by the API util / browser
                return res;
            }
        } catch (error) {
            console.error("Error downloading the file:", error);
            setAlertMessage({
                text: "An error occurred while downloading the file",
                type: "danger",
            });
            setShowAlert(true);
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil((partnership_salesTotalRecords || 0) / itemsPerPage);

    // ✅ Label rules:
    // - If no filters (no shift + no date range) => “(Month to date)”
    // - Else => “(Range)”
    const totalsLabel = useMemo(() => {
        const hasShift = !!String(filters.shiftNumber || "").trim();
        const hasDates = !!(filters.startDate || filters.endDate);
        return !hasShift && !hasDates ? "Month to date" : "Range";
    }, [filters.shiftNumber, filters.startDate, filters.endDate]);

    const SortHint = ({ col, label }) => (
        <span
            style={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => handleSort(col)}
            title="Click to sort"
        >
            {label}{" "}
            {filters.orderByColumn === toPascalCase(col) && (
                <span className="text-muted">{filters.isDescending ? "▼" : "▲"}</span>
            )}
        </span>
    );

    // ===== Full page loading gate (don’t show warnings too early) =====
    if (!authReady) {
        return (
            <>
                <Header />
                <Sidebar />
                <Main>
                    <div className="py-5">
                        <Spinner />
                    </div>
                </Main>
            </>
        );
    }

    return (
        <>
            <Header />
            <Sidebar />
            <Main>
                <SweetAlertWrapper
                    show={showAlert}
                    title="Notification"
                    message={<div>{alertMessage.text}</div>}
                    onConfirm={() => {
                        setShowAlert(false);
                        clear_notification?.();
                    }}
                    confirmBtnText="OK"
                    type={alertMessage.type || "info"}
                />

                {/* ====== Page Header ====== */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <h2 className="mb-1">Partner Sales</h2>
                    </div>

                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" onClick={handleReset} disabled={!userPartnerCode}>
                            Reset
                        </Button>

                        <Button variant="primary" onClick={handleApplyFilters} disabled={loading || !userPartnerCode}>
                            {loading ? "Loading…" : "Apply"}
                        </Button>
                    </div>
                </div>

                {/* ====== Partner missing ====== */}
                {!userPartnerCode && (
                    <Card className="border-0 shadow-sm mb-3">
                        <Card.Body>
                            <h5 className="mb-1">Partner not assigned</h5>
                            <div className="text-muted" style={{ fontSize: 13 }}>
                                Your account has no PartnerCode set. Please ask an admin to assign a PartnerCode to your user.
                            </div>

                            <div className="mt-2" style={{ fontFamily: "monospace", fontSize: 12 }}>
                                UserCode: {user?.userCode || "—"} | Email: {user?.email || "—"}
                            </div>
                        </Card.Body>
                    </Card>
                )}

                {/* ====== Filter Card ====== */}
                <Card className="shadow-sm border-0 mb-3">
                    <Card.Body>
                        <Row className="g-3 align-items-center">
                            <Col md={3}>
                                <Form.Control
                                    type="text"
                                    placeholder="Shift (optional)"
                                    value={filters.shiftNumber}
                                    onChange={(e) => {
                                        setCurrentPage(1);
                                        setFilters((prev) => ({ ...prev, shiftNumber: e.target.value }));
                                    }}
                                    disabled={!userPartnerCode}
                                />
                            </Col>

                            {/* ✅ DatePicker exactly as you asked */}
                            <Col md={2}>
                                <DatePicker
                                    selected={parseDate(filters.startDate)}
                                    onChange={handleDateRangeChange}
                                    startDate={parseDate(filters.startDate)}
                                    endDate={parseDate(filters.endDate)}
                                    selectsRange
                                    className="form-control"
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="Select Date Range"
                                    isClearable
                                    popperPlacement="bottom"
                                    shouldCloseOnSelect={false}
                                    disabled={!userPartnerCode}
                                />
                            </Col>

                            <Col md={4} className="d-flex align-items-end justify-content-end">
                                <div className="d-flex flex-wrap gap-2">
                                    {filters.shiftNumber && (
                                        <Badge bg="secondary" className="px-3 py-2">
                                            Shift: {filters.shiftNumber}
                                        </Badge>
                                    )}
                                    {(filters.startDate || filters.endDate) && (
                                        <Badge bg="secondary" className="px-3 py-2">
                                            Dates: {filters.startDate || "…"} → {filters.endDate || "…"}
                                        </Badge>
                                    )}
                                </div>
                            </Col>

                            <Col md={4} >
                                <Button
                                    className="btn btn-success m-1"
                                    onClick={() =>
                                        handle_downloads(
                                            () =>
                                                download_partnership_sales(
                                                    userPartnerCode,
                                                    "",
                                                    String(filters.shiftNumber || "").trim(),
                                                    filters.startDate || "",
                                                    filters.endDate || "",
                                                    filters.orderByColumn || "DateCreated",
                                                    !!filters.isDescending
                                                ),
                                            setDownloadLoading
                                        )
                                    }
                                    disabled={download_loading || !userPartnerCode}
                                >
                                    {download_loading ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{" "}
                                            Downloading Excel...
                                        </>
                                    ) : (
                                        `Download ${downloadLabel}`
                                    )}
                                </Button>

                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* ====== Summary Cards ====== */}
                <Row className="g-3 mb-3">
                    <Col md={4}>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <div className="text-muted" style={{ fontSize: 13 }}>
                                    Fuelling Events
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 700 }}>{partnership_salesTotalRecords || 0}</div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <div className="text-muted" style={{ fontSize: 13 }}>
                                    ({totalsLabel}) Litres
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 700 }}>{Number(totalLitres || 0).toFixed(2)}</div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <div className="text-muted" style={{ fontSize: 13 }}>
                                    ({totalsLabel}) Amount
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 700 }}>{Number(totalAmounts || 0).toFixed(2)}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* ====== Table ====== */}
                <Card className="shadow-sm border-0">
                    <Card.Body>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div style={{ fontWeight: 700 }}>Sales</div>

                            <div className="d-flex align-items-center gap-2">
                                <Form.Select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setCurrentPage(1);
                                        setItemsPerPage(Number(e.target.value));
                                    }}
                                    style={{ width: 120 }}
                                    disabled={!userPartnerCode}
                                >
                                    {[10, 15, 20, 50].map((n) => (
                                        <option key={n} value={n}>
                                            {n}/page
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                        </div>

                        {loading ? (
                            <Spinner />
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead style={{ position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                                        <tr>
                                            <th style={{ width: 60 }}>#</th>
                                            <th>Sale Id</th>
                                            <th>
                                                <SortHint col="ShiftNumber" label="Shift" />
                                            </th>
                                            <th>Station</th>
                                            <th>
                                                <SortHint col="Quantity" label="Quantity (Litres)" />
                                            </th>
                                            <th>Vehicle</th>
                                            <th>Dispenser</th>
                                            <th>Nozzle</th>
                                            <th>
                                                <SortHint col="SellingPrice" label="Price/Litre(KSH)" />
                                            </th>
                                            <th>
                                                <SortHint col="Amount" label="Amount (KSH)" />
                                            </th>
                                            <th>Payment Type</th>

                                            <th>
                                                <SortHint col="DateCreated" label="Date" />
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {!userPartnerCode ? (
                                            <tr>
                                                <td colSpan="12" className="text-center text-muted py-5">
                                                    PartnerCode is required. Please contact admin to assign your account a PartnerCode.
                                                </td>
                                            </tr>
                                        ) : (partnership_sales || []).length === 0 ? (
                                            <tr>
                                                <td colSpan="12" className="text-center text-muted py-5">
                                                    No sales found for the selected filters.
                                                </td>
                                            </tr>
                                        ) : (
                                            partnership_sales.map((sale, index) => (
                                                <tr key={`${sale.saleId || "x"}-${index}`}>
                                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                    <td style={{ fontFamily: "monospace", fontSize: 12 }}>{sale.saleId || "-"}</td>
                                                    <td>{sale.shiftNumber || "-"}</td>
                                                    <td>
                                                        <div style={{ fontWeight: 600 }}>{sale.stationName || "-"}</div>
                                                        <div className="text-muted" style={{ fontSize: 12 }}>
                                                            {sale.stationCode || ""}
                                                        </div>
                                                    </td>
                                                    <td style={{ fontWeight: 600 }}>{Number(sale.quantity || 0).toFixed(2)}</td>
                                                    <td>{sale.vehicleRegistrationNumber || "-"}</td>
                                                    <td>{sale.dispenserName || "-"}</td>
                                                    <td>{sale.nozzleName || "-"}</td>
                                                    <td style={{ fontWeight: 700 }}>{Number(sale.price || 100).toFixed(2)}</td>
                                                    <td style={{ fontWeight: 700 }}>{Number(sale.amount || 0).toFixed(2)}</td>
                                                    <td>
                                                        <Badge bg="light" text="dark" className="border">
                                                            {sale.paymentTypeName || "Unknown"}
                                                        </Badge>
                                                    </td>

                                                    <td>{sale.dateCreated ? formatteDate(sale.dateCreated) : "-"}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {/* ====== Pagination ====== */}
                <div className="mt-3">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        setCurrentPage={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={partnership_salesTotalRecords}
                    />
                </div>
            </Main>
        </>
    );
};

export default ListPartnershipSales;
